import { observer } from 'mobx-react-lite';
import { ActivityIndicator, FlatList, View } from 'react-native';
import { ICON_NAMES } from '../../../../icon/icon.helper';
import { FocusGroups } from '../../../../focus-provider/FocusProvider.constants';
import { brand } from 'src/theme/colors';
import { CircularButton } from '../../../../buttons/circular-button/CircularButton';
import {
  CIRCULAR_BUTTON_SIZES,
  CIRCULAR_BUTTON_TYPES,
} from '../../../../buttons/circular-button/CircularButton.constants';
import { TestIDs } from 'src/constants/testIDs.constants';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { Arrows, StepInfo } from '../ModalConfigurationSteps.types';
import { useStepCardLogic } from './StepCard.logic';
import { CardsProps } from './StepCard.types';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

export const StepCard = observer(<T extends StepInfo>(props: CardsProps<T>) => {
  const { cards } = props;
  const {
    scrollViewRef,
    initialScrollIndex,
    arrows,
    scrollToIndex,
    activeIndex,
    selectedCard,
    onPressCard,
    onFocusCard,
    styleModal,
    Card,
    onScrollHandler,
  } = useStepCardLogic<T>(props);
  const { isDesktop } = useResponsiveScreen();

  return cards.length ? (
    <View style={[{ width: '100%' }, !isDesktop && { flex: 1 }]}>
      {/* ARROW LEFT */}
      {[Arrows.SHOW_BOTH, Arrows.SHOW_LEFT].includes(arrows) && isDesktop && (
        <CircularButton
          onPress={async () => scrollToIndex(activeIndex - 1)}
          size={CIRCULAR_BUTTON_SIZES.EXTRALARGE}
          type={CIRCULAR_BUTTON_TYPES.ONLY_ICON}
          iconName={ICON_NAMES.ArrowLeft1}
          focusGroup={FocusGroups.MODAL}
          testID={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.LEFT_ARROW)}
          focusId={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.LEFT_ARROW)}
          focusOrder={10}
          iconStyle={{ color: brand.main.water }}
          style={[styleModal.iconButton, { left: 24 }]}
        />
      )}

      <FlatList
        data={cards}
        horizontal={isDesktop}
        style={styleModal.contentWrapper}
        contentContainerStyle={[
          { gap: 16, overflow: 'visible', padding: 0 },
          isDesktop && cards.length < 5 && { width: '100%', justifyContent: 'center' },
          !isDesktop && { flex: 1 },
        ]}
        initialScrollIndex={initialScrollIndex}
        showsHorizontalScrollIndicator={false}
        ref={scrollViewRef}
        keyExtractor={item => item.productConfigurationId}
        getItemLayout={(_, index) => {
          return isDesktop ? { length: 210, offset: 210 * index, index } : { length: 165, offset: 165 * index, index };
        }}
        renderItem={({ item, index }) => {
          return (
            <View
              style={[
                index === 0 && isDesktop && { paddingLeft: 32 },

                index === cards.length - 1 && isDesktop && { paddingRight: 32 },
                index === cards.length - 1 && !isDesktop && { paddingBottom: 32 },
              ]}
            >
              <Card
                item={item}
                index={index}
                onPress={onPressCard}
                onFocus={event => onFocusCard(item, event)}
                isSelected={item.productConfigurationId === selectedCard.productConfigurationId}
                productGroup={props.productGroup}
              />
            </View>
          );
        }}
        onScroll={onScrollHandler}
      />

      {/* ARROW RIGHT */}
      {[Arrows.SHOW_BOTH, Arrows.SHOW_RIGHT].includes(arrows) && isDesktop && (
        <CircularButton
          onPress={async () => scrollToIndex(activeIndex + 1)}
          size={CIRCULAR_BUTTON_SIZES.EXTRALARGE}
          type={CIRCULAR_BUTTON_TYPES.ONLY_ICON}
          iconName={ICON_NAMES.ArrowRight1}
          focusGroup={FocusGroups.MODAL}
          testID={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.RIGHT_ARROW)}
          focusId={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.RIGHT_ARROW)}
          focusOrder={10}
          iconStyle={{ color: brand.main.water }}
          style={[styleModal.iconButton, { right: 24 }]}
        />
      )}
    </View>
  ) : (
    <View style={{ width: 300, height: 340, justifyContent: 'center', alignItems: 'center' }}>
      <ActivityIndicator size={60} color={brand.main.water} />
    </View>
  );
});
